import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ReviewResults from "@/components/ReviewResults";
import { AdminLayout } from "@/layouts/AdminLayout";
import { DashboardLayout } from "@/layouts/DashboardLayout";
import { api } from "@/services/api";
import type { Review, ReviewResult, Submission } from "@/types";

interface ReviewPageProps {
  layout?: "dashboard" | "admin";
}

const transformReviewToResult = (review: Review, submission: Submission): ReviewResult => {
  const meta = review.sections?._meta as any;

  const sections = review.sections
    ? Object.entries(review.sections)
        .filter(([key]) => key !== "_meta")
        .map(([key, value]: [string, any]) => ({
          name: key
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ")
            .replace("And", "&"),
          score: value.score || 0,
          band: value.band || value.command_word_level,
          behaviour_demonstrated: value.behaviour_demonstrated,
          observable_features_detected:
            value.observable_features_detected || value.detected_behaviours || [],
          missing_features_for_next_band:
            value.missing_features_for_next_band || value.missing_behaviours || [],
          diagnostic_explanation: value.diagnostic_explanation || value.short_rationale || "",
        }))
    : [];

  return {
    isRelevant: meta?.is_relevant ?? true,
    relevanceExplanation: meta?.relevance_explanation ?? "",
    overallScore: review.final_score,
    sections,
    criteriaScores: sections.map((section, index) => ({
      criterionId: `criterion-${index}`,
      criterionName: section.name,
      score: section.score,
      comments: section.diagnostic_explanation || "",
    })),
    timestamp: new Date(review.reviewed_at || review.created_at),
    fileName:
      submission.original_filename ?? submission.file_path.split("/").pop() ?? "submission.pdf",
    submissionId: submission.id,
  };
};

export default function ReviewPage({ layout = "dashboard" }: ReviewPageProps) {
  const Layout = layout === "admin" ? AdminLayout : DashboardLayout;
  const { reviewId } = useParams<{ reviewId: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [reviewResult, setReviewResult] = useState<ReviewResult | null>(null);
  const [aiReviewResult, setAiReviewResult] = useState<ReviewResult | null>(null);
  const [showAiReview, setShowAiReview] = useState(false);
  const [review, setReview] = useState<Review | null>(null);

  const loadReview = useCallback(async () => {
    if (!reviewId) return;

    try {
      const reviewData = await api.getReview(reviewId);
      const submission = await api.getSubmission(reviewData.submission_id);
      setReview(reviewData);

      if (reviewData && submission) {
        const isAdminReviewed =
          reviewData.status === "admin_confirmed" || reviewData.status === "admin_updated";

        if (isAdminReviewed && reviewData.admin_sections) {
          // Show admin review as primary
          const adminResult = transformReviewToResult(
            {
              ...reviewData,
              sections: reviewData.admin_sections,
              final_score: reviewData.admin_final_score ?? reviewData.final_score,
            },
            submission
          );
          setReviewResult(adminResult);

          // Keep AI review for toggle
          const aiResult = transformReviewToResult(reviewData, submission);
          setAiReviewResult(aiResult);
        } else {
          const result = transformReviewToResult(reviewData, submission);
          setReviewResult(result);
        }
      }
    } catch (error) {
      console.error("Failed to load review:", error);
    } finally {
      setIsLoading(false);
    }
  }, [reviewId]);

  useEffect(() => {
    loadReview();
  }, [loadReview]);

  const isAdminReviewed =
    review?.status === "admin_confirmed" || review?.status === "admin_updated";

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </Layout>
    );
  }

  if (!reviewResult) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <p className="text-gray-500">Review not found</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <ReviewResults
        result={reviewResult}
        variant={isAdminReviewed ? "final" : "ai"}
        assessorReviewedAt={
          isAdminReviewed && review?.admin_reviewed_at ? new Date(review.admin_reviewed_at) : null
        }
      />

      {/* AI Review Toggle - only when admin review is primary */}
      {isAdminReviewed && aiReviewResult && (
        <div className="max-w-2xl mx-auto px-4 pb-8">
          <div className="border-t border-gray-200 pt-4">
            <button
              onClick={() => setShowAiReview(!showAiReview)}
              className="flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm transition-colors"
            >
              {showAiReview ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
              View AI Feedback
            </button>

            {showAiReview && (
              <div className="mt-4">
                <ReviewResults result={aiReviewResult} variant="ai" />
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}
